import { Application } from "../../App";
import { appPaths } from "../../Common";
import { Icon } from "../../Common/Types/Icon";
import { FeedbackParent } from "../../Feedback/Constants/feedbackEnums";
import { appIconService } from "../../App/Services/AppIconService";
import { Company } from "../../Company/Models/Company";

export type SidebarItem = LinkSidebarItem | GroupSidebarItem | OtherSidebarItem;

export type LinkSidebarItem = {
  name: string;
  icon: Icon;
  path: string;
  isInternal: boolean;
  isGroup: boolean;
  createPath?: string;
};

export type GroupSidebarItem = {
  name: string;
  icon: Icon;
  path: string;
  isInternal: boolean;
  isGroup: boolean;
  children: SidebarItem[];
  matchPaths: string[];
};

export type OtherSidebarItem = {
  isSpecial: boolean;
  type: string;
};

export const otherSidebarItems: Record<string, () => OtherSidebarItem> = {
  divider: () => ({
    isSpecial: true,
    type: "divider"
  }),
  contextSelector: () => ({
    isSpecial: true,
    type: "contextSelector"
  })
};

export const linkSideBarItems: Record<string, (...args: any) => LinkSidebarItem> = {
  // apps
  appSubLink: (application: Application) => ({
    name: application.name,
    icon: appIconService.getIconForApp(application.rolePrefix),
    path: appPaths.app.root.replace(":id", application.rolePrefix),
    isInternal: true,
    isGroup: false
  }),
  appAllFeedback: (appCode: string) => ({
    name: "All",
    icon: {
      appName: "GeneralLayout",
      assetName: "GENERAL_L_Public_20"
    },
    path: appPaths.app.root.replace(":id", appCode),
    isInternal: true,
    isGroup: false
  }),
  appFeatureRequests: (appCode: string) => ({
    name: "Feature Requests",
    icon: {
      appName: "cmBuilder",
      assetName: "VIEWER_L_Utilities_Lighting"
    },
    path: appPaths.app.featureRequests.replace(":id", appCode),
    isInternal: true,
    isGroup: false
  }),
  appBugReports: (appCode: string) => ({
    name: "Bug Reports",
    icon: {
      appName: "cmFeedback",
      assetName: "SIDEBAR_L_Bugs"
    },
    path: appPaths.app.bugReports.replace(":id", appCode),
    isInternal: true,
    isGroup: false
  }),
  appResourceRequests: (appCode: string) => ({
    name: "Resource Requests",
    icon: {
      appName: "GeneralLayout",
      assetName: "GENERAL_L_BoundingBox"
    },
    path: appPaths.app.resourceRequests.replace(":id", appCode),
    isInternal: true,
    isGroup: false
  }),

  // topics
  topicsList: () => ({
    name: "Company Topics",
    icon: {
      appName: "cmFeedback",
      assetName: "SIDEBAR_F_Topics"
    },
    path: appPaths.topic.root,
    isInternal: true,
    isGroup: false
  }),
  topicDashboard: (topicCode: string) => ({
    name: "Feedback",
    icon: {
      appName: "GeneralLayout",
      assetName: "GENERAL_L_Feedback"
    },
    path: appPaths.topic.featureRequests.replace(":topicCode", topicCode),
    isInternal: true,
    isGroup: false
  }),
  topicAllFeedback: (topicCode: string) => ({
    name: "All",
    icon: {
      appName: "GeneralLayout",
      assetName: "GENERAL_L_Public_20"
    },
    path: appPaths.topic.featureRequests.replace(":topicCode", topicCode),
    isInternal: true,
    isGroup: false
  }),

  // tags
  tagsList: () => ({
    name: "Tags",
    icon: {
      appName: "cmKnowledge",
      assetName: "SIDEBAR_F_Tag"
    },
    path: appPaths.tags.root,
    isInternal: true,
    isGroup: false
  }),

  // settings
  companySettings: (company: Company) => ({
    name: "Company Settings",
    icon: {
      appName: "cmKnowledge",
      assetName: "SIDEBAR_F_CompanySetting"
    },
    path: appPaths.company.settings.replace(":shortname", company.shortName),
    isInternal: true,
    isGroup: false
  }),

  // overview
  overview: () => ({
    name: "Overview",
    icon: {
      appName: "cmKnowledge",
      assetName: "SIDEBAR_F_Overview"
    },
    path: appPaths.overview.root,
    isInternal: true,
    isGroup: false
  })
};

// these sidebar items represent groups of other sidebar items- their path should be the same as the default item in the group
export const groupSidebarItems: Record<string, (...args: any) => GroupSidebarItem> = {
  apps: (availableApps: Application[]) => ({
    name: "Subscribed Apps",
    icon: {
      appName: "cmKnowledge",
      assetName: "SIDEBAR_F_SubscribedApps"
    },
    path: appPaths.root, // TODO this should be its own page
    isInternal: true,
    isGroup: true,
    children: availableApps.map(a => linkSideBarItems.appSubLink(a)),
    matchPaths: [appPaths.root]
  }),
  feedback: (context: FeedbackParent, contextItemCode: string) => ({
    name: "Feedback",
    icon: {
      appName: "GeneralLayout",
      assetName: "GENERAL_L_Feedback"
    },
    path:
      context === FeedbackParent.Application
        ? appPaths.app.root.replace(":id", contextItemCode)
        : appPaths.topic.featureRequests.replace(":topicCode", contextItemCode),
    isInternal: true,
    isGroup: true,
    children: [
      ...(context === FeedbackParent.Application
        ? [
            linkSideBarItems.appAllFeedback(contextItemCode),
            linkSideBarItems.appFeatureRequests(contextItemCode),
            linkSideBarItems.appBugReports(contextItemCode),
            linkSideBarItems.appResourceRequests(contextItemCode)
          ]
        : []),
      ...(context === FeedbackParent.Topic
        ? [linkSideBarItems.topicAllFeedback(contextItemCode)]
        : [])
    ],
    matchPaths: [
      linkSideBarItems.appAllFeedback(contextItemCode).path,
      linkSideBarItems.appFeatureRequests(contextItemCode).path,
      linkSideBarItems.appBugReports(contextItemCode).path,
      linkSideBarItems.appResourceRequests(contextItemCode).path,
      linkSideBarItems.topicAllFeedback(contextItemCode).path
    ]
  })
};
